import * as React from "react";

import { ITheme, mergeStyles } from "@fluentui/react/lib/Styling";
import { KeyCodes, memoizeFunction } from "@fluentui/react/lib/Utilities";

import {
  dataUniqueId,
  getStandardControlSemanticColorsWithTheme,
  StandardControlSemanticColors,
  UILocation,
} from "@1js/acui-common";
import { AppContext, AppContextType } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { AppThemeConsumer } from "@1js/acui-theme";

import { FocusableDivProps } from "./FocusableDiv.props";

function handleDivClick(wrapperElementId: string, type: string) {
  if (wrapperElementId) {
    const focusableDivWrapperElement = document.querySelector(
      "div[data-unique-id=" + wrapperElementId + "]"
    );
    if (focusableDivWrapperElement !== null) {
      switch (type) {
        case "AppComboBoxProps":
          const menuDropdownButton: HTMLButtonElement | null =
            focusableDivWrapperElement.querySelector(
              "button[class*=ms-ComboBox-CaretDown-button]"
            );
          menuDropdownButton && menuDropdownButton.click();
          break;
        case "AppSpinButtonProps":
          const inputElement: HTMLButtonElement | null =
            focusableDivWrapperElement.querySelector("input[type=text]");
          inputElement && inputElement.focus();
          break;
        default:
          break;
      }
    }
  }
}

function onDivKeyDown(
  wrapperElementId: string,
  type: string
): (ev: React.KeyboardEvent<HTMLDivElement>) => void {
  return (ev: React.KeyboardEvent<HTMLDivElement>): void => {
    if (
      ev.which === KeyCodes.enter ||
      (ev.altKey && ev.which === KeyCodes.down)
    ) {
      handleDivClick(wrapperElementId, type);
      ev.stopPropagation();
      ev.preventDefault();
    } else if (
      ev.which === KeyCodes.escape ||
      (ev.altKey && ev.which === KeyCodes.up)
    ) {
      const focusableDivWrapperElement: HTMLDivElement | null =
        document.querySelector("div[data-unique-id=" + wrapperElementId + "]");
      focusableDivWrapperElement && focusableDivWrapperElement.focus();
      ev.stopPropagation();
      ev.preventDefault();
    }
  };
}

function getDivRole(
  type: string,
  dataNav?: boolean,
  location?: string
): string {
  if (dataNav === true) {
    return "option";
  }

  if (
    location &&
    (location === UILocation.Ribbon ||
      location === UILocation.SingleLineRibbon ||
      location === UILocation.MultilineRibbon ||
      location === UILocation.Floatie)
  ) {
    switch (type) {
      case "AppComboBoxProps":
        return "combobox";
      case "AppSpinButtonProps":
        return "spinbutton";
    }
  }
  return "menuItem";
}

const getDivClassNames = memoizeFunction((theme: ITheme): string => {
  const standardControlSemanticColors: StandardControlSemanticColors =
    getStandardControlSemanticColorsWithTheme(theme);
  return mergeStyles({
    selectors: {
      ":focus": {
        outline: "0px",
        border: "1px solid",
        background: standardControlSemanticColors.hoverBackgroundColor,
        borderColor: standardControlSemanticColors.focusedBorderColor,
      },
      ":not(:focus)": {
        border: "1px solid transparent",
      },
    },
  });
});

function shouldActivateControlOnEnter(
  context: AppContextType
): boolean | undefined {
  return checkExperiment(
    context,
    ribbonExperiments.activateComboAndSpinControlOnEnter
  );
}

const getMemoizedFocusableDivProps = memoizeFunction(
  (
    theme: ITheme,
    context: AppContextType,
    divWrapperElementId: string,
    type: string,
    location?: string,
    label?: string,
    disabled?: boolean,
    dataNav?: boolean
  ): any => {
    if (shouldActivateControlOnEnter(context)) {
      return {
        tabIndex: -1,
        className: getDivClassNames(theme),
        onKeyDown: onDivKeyDown(divWrapperElementId, type),
        role: getDivRole(type, dataNav, location),
        "data-nav": dataNav ? true : false,
        "data-is-focusable": true,
        "data-unique-id": divWrapperElementId,
        "aria-disabled": disabled,
        "aria-label": label,
      };
    }
    return {};
  }
);

export function focusableDiv<
  OriginalProps extends FocusableDivProps,
  TComponentState
>(Component: React.ComponentClass) {
  class DivWrapper extends React.PureComponent<OriginalProps, TComponentState> {
    public render(): JSX.Element {
      const { innerRef, ...props } = this.props;
      const divWrapperElementId: string = dataUniqueId(props) + props.type;
      return (
        <AppThemeConsumer>
          {(theme: ITheme) => (
            <AppContext.Consumer>
              {(context) => (
                <>
                  {shouldActivateControlOnEnter(context) && (
                    <div
                      {...getMemoizedFocusableDivProps(
                        theme,
                        context,
                        divWrapperElementId,
                        props.type,
                        props.location,
                        props.label,
                        props.disabled,
                        props.dataNav
                      )}
                    >
                      <Component {...props} ref={innerRef} />
                    </div>
                  )}
                  {!shouldActivateControlOnEnter(context) && (
                    <Component {...props} ref={innerRef} />
                  )}
                </>
              )}
            </AppContext.Consumer>
          )}
        </AppThemeConsumer>
      );
    }
  }

  return React.forwardRef(
    (props: OriginalProps, forwardRef: React.RefObject<React.Component>) => {
      return <DivWrapper {...props} innerRef={forwardRef} />;
    }
  );
}

export const getMemoizedInputProps = memoizeFunction(
  (context: AppContextType, dataNav?: boolean): any => {
    return {
      "data-nav":
        dataNav && !shouldActivateControlOnEnter(context) ? true : undefined,
      "data-is-focusable": shouldActivateControlOnEnter(context)
        ? false
        : undefined,
    };
  }
);
